import { z } from 'zod';

// src/spam/thresholds.ts

// src/spam/spam-thresholds.generated.json
var spam_thresholds_generated_default = {
  campaign: {
    min_recipients: 15,
    min_sends: 20
  },
  dust_usd: 0.01,
  fallback_family: "evm",
  native_dust_max_by_chain: {},
  native_dust_max_by_coin: {
    BTC: {
      amount: "1000",
      decimals: 8
    }
  },
  native_dust_max_by_family: {
    evm: "500000000000000",
    hypercore: "1000",
    solana: "500000",
    tron: "1000000",
    utxo: "1000"
  },
  native_dust_settled_max: {
    amount: "10000000000000",
    decimals: 18
  },
  native_proof_min_by_family: {
    evm: "1000000000000000",
    hypercore: "10000",
    solana: "1000000",
    tron: "10000000",
    utxo: "10000"
  },
  token_dust_raw_max: "1000"
};

// src/spam/thresholds.ts
var RawAmount = z.string().regex(/^\d+$/, "a raw base-unit amount is a decimal string").transform((value) => BigInt(value));
var StatedAmount = z.object({
  amount: RawAmount,
  decimals: z.number().int().nonnegative()
});
var SpamThresholdsSchema = z.object({
  dust_usd: z.number().positive(),
  fallback_family: z.string().min(1),
  native_dust_max_by_family: z.record(z.string(), RawAmount),
  native_dust_settled_max: StatedAmount,
  native_dust_max_by_coin: z.record(z.string(), StatedAmount),
  native_dust_max_by_chain: z.record(z.string(), RawAmount),
  token_dust_raw_max: RawAmount,
  native_proof_min_by_family: z.record(z.string(), RawAmount),
  campaign: z.object({
    min_sends: z.number().int().positive(),
    min_recipients: z.number().int().positive()
  })
}).refine((t) => t.native_proof_min_by_family[t.fallback_family] !== void 0, {
  message: "the fallback family needs a native proof floor"
});
var SPAM_THRESHOLDS = Object.freeze(SpamThresholdsSchema.parse(spam_thresholds_generated_default));
var DUST_USD_FLOOR = SPAM_THRESHOLDS.dust_usd;
var CAMPAIGN_MIN_SENDS = SPAM_THRESHOLDS.campaign.min_sends;
var CAMPAIGN_MIN_RECIPIENTS = SPAM_THRESHOLDS.campaign.min_recipients;
var SPAM_FALLBACK_FAMILY = SPAM_THRESHOLDS.fallback_family;
var NATIVE_PROOF_MIN_BY_FAMILY = Object.freeze({
  ...SPAM_THRESHOLDS.native_proof_min_by_family
});
var FALLBACK_NATIVE_PROOF_MIN = NATIVE_PROOF_MIN_BY_FAMILY[SPAM_FALLBACK_FAMILY];
var NATIVE_DUST_MAX_BY_FAMILY = Object.freeze({
  ...SPAM_THRESHOLDS.native_dust_max_by_family
});
function nativeProofMinForFamily(family) {
  const key = family || SPAM_FALLBACK_FAMILY;
  return NATIVE_PROOF_MIN_BY_FAMILY[key] ?? null;
}

// src/dust/index.ts
var BARE_TRANSFER_PROTOCOLS = /* @__PURE__ */ new Set([
  "native_transfer",
  "erc20",
  "erc721",
  "erc1155"
]);
var FALLBACK_PROTOCOL_ID = "generic";
var NON_PROTOCOL_IDS = /* @__PURE__ */ new Set([
  "native_transfer",
  "erc20",
  "erc721",
  "erc1155",
  "erc4626",
  "generic"
]);
function claimedProtocols(row) {
  const primary = row.primaryProtocolId || "";
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const id of [primary, ...row.protocolIds ?? []]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (id === FALLBACK_PROTOCOL_ID && id !== primary) continue;
    out.push(id);
  }
  return out;
}
function isBareTransfer(row) {
  const claimed = claimedProtocols(row);
  return claimed.length > 0 && claimed.every((id) => BARE_TRANSFER_PROTOCOLS.has(id));
}
var DEFAULT_DUST_USD = DUST_USD_FLOOR;
function pricedAboveDust(valueUsd, floor = DEFAULT_DUST_USD) {
  return typeof valueUsd === "number" && Number.isFinite(valueUsd) && valueUsd >= floor;
}
var DEFAULT_CAMPAIGN_MIN_RECIPIENTS = CAMPAIGN_MIN_RECIPIENTS;
function dustReason(row, options = {}) {
  const floor = options.usdFloor ?? DEFAULT_DUST_USD;
  if (options.protectedIds && row.canonicalTxId && options.protectedIds.has(row.canonicalTxId)) {
    return null;
  }
  const usd = typeof row.valueUsd === "number" && Number.isFinite(row.valueUsd) ? row.valueUsd : null;
  const sender = (row.fromAddress ?? "").toLowerCase();
  const fromDuster = Boolean(sender && options.dustSenders?.has(sender));
  if (usd === null && !fromDuster) return null;
  if (usd !== null && usd >= floor) return null;
  if (!isBareTransfer(row)) return null;
  if (fromDuster) {
    return "Bare transfer from an address seen sending these in bulk \u2014 the shape of a dusting campaign.";
  }
  return `Bare transfer worth ${usd === 0 ? "nothing" : `under $${floor}`} \u2014 the shape of a dusting transfer.`;
}
function withoutDust(rows, read, options = {}) {
  const kept = [];
  let filtered = 0;
  let reason = null;
  for (const row of rows) {
    const why = dustReason(read(row), options);
    if (why === null) {
      kept.push(row);
      continue;
    }
    filtered += 1;
    reason = reason ?? why;
  }
  return { kept, filtered, reason };
}
function deriveDustSenders(rows, read, options = {}) {
  const minRecipients = options.minRecipients ?? DEFAULT_CAMPAIGN_MIN_RECIPIENTS;
  const recipients = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const candidate = read(row);
    const sender = (candidate.fromAddress ?? "").toLowerCase();
    const recipient = (candidate.toAddress ?? "").toLowerCase();
    if (!sender || !recipient || sender === recipient) continue;
    if (!isBareTransfer(candidate)) continue;
    const seen = recipients.get(sender) ?? /* @__PURE__ */ new Set();
    seen.add(recipient);
    recipients.set(sender, seen);
  }
  const out = /* @__PURE__ */ new Set();
  for (const [sender, seen] of recipients) {
    if (seen.size >= minRecipients) out.add(sender);
  }
  return out;
}

export { BARE_TRANSFER_PROTOCOLS, CAMPAIGN_MIN_RECIPIENTS, CAMPAIGN_MIN_SENDS, DEFAULT_CAMPAIGN_MIN_RECIPIENTS, DEFAULT_DUST_USD, DUST_USD_FLOOR, FALLBACK_NATIVE_PROOF_MIN, FALLBACK_PROTOCOL_ID, NATIVE_DUST_MAX_BY_FAMILY, NATIVE_PROOF_MIN_BY_FAMILY, NON_PROTOCOL_IDS, SPAM_FALLBACK_FAMILY, SPAM_THRESHOLDS, claimedProtocols, deriveDustSenders, dustReason, nativeProofMinForFamily, pricedAboveDust, withoutDust };
//# sourceMappingURL=chunk-EBNQFYRL.js.map
//# sourceMappingURL=chunk-EBNQFYRL.js.map